// src/pages/ProgramsFullSection/ProgramsFullSection.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './ProgramsFullSection.css';
import { FaCalendarAlt, FaChevronDown, FaChevronUp, FaImages, FaTimes} from 'react-icons/fa';
import { events } from '../../data/programsData'; 
import { FeatureIcon } from '../../components';
import { compareAsc, endOfDay } from 'date-fns';

// Importa os componentes e estilos do Swiper para o Lightbox
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';


const ProgramsFullSection = () => {
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentLightboxImages, setCurrentLightboxImages] = useState([]);
  const [initialSlide, setInitialSlide] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.startsWith('#evento-')) {
      const eventId = parseInt(hash.replace('#evento-', ''), 10);
      if (!isNaN(eventId)) {
        setExpandedEvent(eventId);
        setTimeout(() => {
          const element = document.getElementById(`evento-${eventId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [location.hash]);

  const toggleEvent = (eventId) => {
    setExpandedEvent(prev => (prev === eventId ? null : eventId));
  };
  
  const openLightbox = useCallback((images, index = 0) => {
    setCurrentLightboxImages(images);
    setInitialSlide(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const now = new Date();
  const activeAndSortedEvents = events
    .map(yearData => {
      const futureItems = yearData.items
        .filter(event => endOfDay(new Date(event.endDate)) >= now)
        .sort((a, b) => compareAsc(new Date(a.startDate), new Date(b.startDate)));
      return { ...yearData, items: futureItems };
    })
    .filter(yearData => yearData.items.length > 0);

  return (
    <section id="programacao-completa" className="programs-section">
      <div className="container">
        <div className="section-header">
            <span className="section-eyebrow">Agenda Exclusiva</span>
            <h1 className="section-title">Programação Anual Completa</h1>
            <p className="section-subtitle">Todos os eventos especiais que transformam sua estadia em uma experiência única</p>
        </div>
        
        <div className="timeline">
          {activeAndSortedEvents.map((yearData) => (
            <React.Fragment key={yearData.year}>
              <div className="year-marker">
                <div className="year-label">{yearData.year}</div>
                <div className="year-line"></div>
              </div>
              <div className="events-container">
                {yearData.items.map((event) => (
                  <div key={event.id} id={`evento-${event.id}`} className={`event-item ${expandedEvent === event.id ? 'expanded' : ''}`}>
                    <div className="event-date">
                      <FaCalendarAlt className="calendar-icon" />
                      <span>{event.dateString}</span>
                    </div>
                    <div className="event-content">
                      <div className="event-image">
                        <img src={event.image} alt={event.title} />
                      </div>
                      <div className="event-details">
                        <div className="event-header">
                          
                          <h3 className="event-title">{event.title}</h3>
                        </div>
                        <p className="event-description">{event.description}</p>
                        
                        {expandedEvent === event.id && event.details && (
                          <div className="event-expanded">
                            {event.details.comidas && (
                              <div className="details-section">
                                <h4><FeatureIcon iconName={'FaUtensils'} /> Comidas Típicas</h4>
                                <div className="details-columns">{event.details.comidas.map((item, i) => <div key={i} className="detail-item">{item}</div>)}</div>
                              </div>
                            )}
                             {event.details.musicas && (
                              <div className="details-section">
                                <h4><FeatureIcon iconName={'FaMusic'} /> Músicas e Ritmos</h4>
                                <div className="details-columns">{event.details.musicas.map((item, i) => <div key={i} className="detail-item">{item}</div>)}</div>
                              </div>
                            )}
                             {event.details.atividades && (
                              <div className="details-section">
                                <h4><FeatureIcon iconName={'FaStar'} /> Atividades</h4>
                                <div className="details-columns">{event.details.atividades.map((item, i) => <div key={i} className="detail-item">{item}</div>)}</div>
                              </div>
                            )}
                            {event.details.imagens && event.details.imagens.length > 0 && (
                              <div className="details-section">
                                <h4><FaImages /> Galeria</h4>
                                <div className="additional-gallery">
                                  {event.details.imagens.map((img, idx) => (
                                    <div key={idx} className="gallery-item" onClick={() => openLightbox(event.details.imagens, idx)}>
                                      <img src={img} alt={`${event.title} ${idx + 1}`} />
                                      <div className="gallery-overlay"><FaImages className="gallery-icon" /></div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="event-toggle-container">
                          {expandedEvent === event.id && (
                            <a href="https://wa.me/+5535988179138" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                                Faça sua Reserva
                            </a>
                          )}
                          <div className="event-toggle" onClick={() => toggleEvent(event.id)}>
                            <span>{expandedEvent === event.id ? 'Ver menos' : 'Ver mais detalhes'}</span>
                            {expandedEvent === event.id ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}><FaTimes /></button>
            <div className="lightbox-swiper-container">
              <Swiper
                modules={[Navigation, Pagination, A11y]}
                navigation
                pagination={{ type: 'fraction' }}
                loop={true}
                initialSlide={initialSlide}
                className="lightbox-swiper"
              >
                {currentLightboxImages.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img src={img} alt={`Imagem ${index + 1}`} className="lightbox-image"/>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

          </div>
        </div>
      )}
    </section>
  );
};

export default ProgramsFullSection;